import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const LanguageContext = createContext();

const translations = {
  en: {
    // Navigation
    nav_dashboard: 'Dashboard',
    nav_jobs: 'Jobs',
    nav_messages: 'Messages',
    nav_profile: 'Profile',
    nav_logout: 'Logout',
    
    // Landing Page
    landing_hero_title: 'Connect with On-Demand Kitchen Staff',
    landing_hero_subtitle: 'HandsOnTheFly connects restaurants with skilled culinary professionals in Minneapolis and Detroit',
    landing_cta_restaurant: 'I need kitchen staff',
    landing_cta_worker: 'I\'m looking for work',
    landing_how_it_works: 'How It Works',
    landing_testimonials: 'What Our Users Say',
    
    // Common
    common_loading: 'Loading...',
    common_error: 'An error occurred',
    common_success: 'Success',
    common_cancel: 'Cancel',
    common_save: 'Save',
    common_submit: 'Submit',
    common_continue: 'Continue',
    common_back: 'Back',
    common_next: 'Next',
    common_email: 'Email',
    common_password: 'Password',
    common_phone: 'Phone Number',
    common_name: 'Name',
    common_address: 'Address',
    common_city: 'City',
    common_state: 'State',
    common_zip: 'ZIP Code',
    common_bio: 'Bio',
    common_skills: 'Skills',
    common_rate: 'Hourly Rate',
    common_experience: 'Experience',
    
    // Authentication
    auth_login: 'Login',
    auth_signup: 'Sign Up',
    auth_signin_title: 'Sign in to your account',
    auth_signup_title: 'Create your account',
    auth_signup_as: 'I am signing up as',
    auth_signup_restaurant: 'Restaurant',
    auth_signup_worker: 'Worker',
    auth_verify_phone: 'Verify Phone Number',
    auth_verify_code: 'Enter the verification code sent to your phone',
    auth_resend_code: 'Resend Code',
    auth_no_account: 'Don\'t have an account',
    
    // Jobs
    jobs_browse: 'Browse Jobs',
    jobs_post: 'Post a Job',
    jobs_my_jobs: 'My Jobs',
    jobs_applications: 'Applications',
    jobs_title: 'Job Title',
    jobs_description: 'Description',
    jobs_start_time: 'Start Time',
    jobs_end_time: 'End Time',
    jobs_hourly_rate: 'Hourly Rate',
    jobs_required_skills: 'Required Skills',
    jobs_emergency_level: 'Priority',
    jobs_normal: 'Normal',
    jobs_urgent: 'Urgent',
    jobs_apply: 'Apply Now',
    jobs_view_details: 'View Details',
    
    // Messages
    messages_title: 'Messages',
    messages_new_message: 'New Message',
    messages_type_message: 'Type your message...',
    messages_send: 'Send',
    
    // Profile
    profile_title: 'Profile',
    profile_edit: 'Edit Profile',
    profile_restaurant_info: 'Restaurant Information',
    profile_worker_info: 'Worker Information',
    profile_operating_hours: 'Operating Hours',
    profile_cuisine_types: 'Cuisine Types',
    profile_availability: 'Availability',
    profile_travel_radius: 'Travel Radius',
    profile_upload_logo: 'Upload Logo',
    profile_upload_photo: 'Upload Photo',
    
    // Ratings
    rating_title: 'Leave a Rating',
    rating_rate_experience: 'Rate your experience',
    rating_leave_comment: 'Leave a comment (optional)',
    rating_submit: 'Submit Rating',
    
    // Payments
    payment_title: 'Payment',
    payment_amount: 'Amount',
    payment_platform_fee: 'Platform Fee (10%)',
    payment_worker_payout: 'Worker Payout',
    payment_process: 'Process Payment',
    payment_setup_stripe: 'Set up payments with Stripe',
    
    // Notifications
    notification_new_job: 'New job opportunity',
    notification_new_message: 'New message',
    notification_application_accepted: 'Your application was accepted',
    notification_application_rejected: 'Your application was not selected',
    notification_job_completed: 'Job completed',
    notification_payment_received: 'Payment received',
    notification_new_rating: 'New rating received'
  },
  
  es: {
    // Navigation
    nav_dashboard: 'Panel de Control',
    nav_jobs: 'Trabajos',
    nav_messages: 'Mensajes',
    nav_profile: 'Perfil',
    nav_logout: 'Cerrar Sesión',
    
    // Landing Page
    landing_hero_title: 'Conecta con Personal de Cocina a Demanda',
    landing_hero_subtitle: 'HandsOnTheFly conecta restaurantes con profesionales culinarios calificados en Minneapolis y Detroit',
    landing_cta_restaurant: 'Necesito personal de cocina',
    landing_cta_worker: 'Busco trabajo',
    landing_how_it_works: 'Cómo Funciona',
    landing_testimonials: 'Lo Que Dicen Nuestros Usuarios',
    
    // Common
    common_loading: 'Cargando...',
    common_error: 'Ocurrió un error',
    common_success: 'Éxito',
    common_cancel: 'Cancelar',
    common_save: 'Guardar',
    common_submit: 'Enviar',
    common_continue: 'Continuar',
    common_back: 'Atrás',
    common_next: 'Siguiente',
    common_email: 'Correo Electrónico',
    common_password: 'Contraseña',
    common_phone: 'Número de Teléfono',
    common_name: 'Nombre',
    common_address: 'Dirección',
    common_city: 'Ciudad',
    common_state: 'Estado',
    common_zip: 'Código Postal',
    common_bio: 'Biografía',
    common_skills: 'Habilidades',
    common_rate: 'Tarifa por Hora',
    common_experience: 'Experiencia',
    
    // Authentication
    auth_login: 'Iniciar Sesión',
    auth_signup: 'Registrarse',
    auth_signin_title: 'Inicia sesión en tu cuenta',
    auth_signup_title: 'Crea tu cuenta',
    auth_signup_as: 'Me estoy registrando como',
    auth_signup_restaurant: 'Restaurante',
    auth_signup_worker: 'Worker',
    auth_verify_phone: 'Verificar Número de Teléfono',
    auth_verify_code: 'Ingresa el código de verificación enviado a tu teléfono',
    auth_resend_code: 'Reenviar Código',
    auth_no_account: '¿No tienes una cuenta',
    
    // Jobs
    jobs_browse: 'Buscar Trabajos',
    jobs_post: 'Publicar Trabajo',
    jobs_my_jobs: 'Mis Trabajos',
    jobs_applications: 'Solicitudes',
    jobs_title: 'Título del Trabajo',
    jobs_description: 'Descripción',
    jobs_start_time: 'Hora de Inicio',
    jobs_end_time: 'Hora de Finalización',
    jobs_hourly_rate: 'Tarifa por Hora',
    jobs_required_skills: 'Habilidades Requeridas',
    jobs_emergency_level: 'Prioridad',
    jobs_normal: 'Normal',
    jobs_urgent: 'Urgente',
    jobs_apply: 'Aplicar Ahora',
    jobs_view_details: 'Ver Detalles',
    
    // Messages
    messages_title: 'Mensajes',
    messages_new_message: 'Nuevo Mensaje',
    messages_type_message: 'Escribe tu mensaje...',
    messages_send: 'Enviar',
    
    // Profile
    profile_title: 'Perfil',
    profile_edit: 'Editar Perfil',
    profile_restaurant_info: 'Información del Restaurante',
    profile_worker_info: 'Información del Trabajador',
    profile_operating_hours: 'Horarios de Operación',
    profile_cuisine_types: 'Tipos de Cocina',
    profile_availability: 'Disponibilidad',
    profile_travel_radius: 'Radio de Viaje',
    profile_upload_logo: 'Subir Logo',
    profile_upload_photo: 'Subir Foto',
    
    // Ratings
    rating_title: 'Dejar una Calificación',
    rating_rate_experience: 'Califica tu experiencia',
    rating_leave_comment: 'Deja un comentario (opcional)',
    rating_submit: 'Enviar Calificación',
    
    // Payments
    payment_title: 'Pago',
    payment_amount: 'Cantidad',
    payment_platform_fee: 'Comisión de la Plataforma (10%)',
    payment_worker_payout: 'Pago al Trabajador',
    payment_process: 'Procesar Pago',
    payment_setup_stripe: 'Configurar pagos con Stripe',
    
    // Notifications
    notification_new_job: 'Nueva oportunidad de trabajo',
    notification_new_message: 'Nuevo mensaje',
    notification_application_accepted: 'Tu solicitud fue aceptada',
    notification_application_rejected: 'Tu solicitud no fue seleccionada',
    notification_job_completed: 'Trabajo completado',
    notification_payment_received: 'Pago recibido',
    notification_new_rating: 'Nueva calificación recibida'
  }
};

export function LanguageProvider({ children }) {
  const { user } = useAuth();
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    // Set language from user preference or browser locale
    if (user?.language_preference) {
      setLanguage(user.language_preference);
    } else {
      const browserLang = navigator.language.split('-')[0];
      if (translations[browserLang]) {
        setLanguage(browserLang);
      }
    }
  }, [user]);

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  const changeLanguage = (newLanguage) => {
    if (translations[newLanguage]) {
      setLanguage(newLanguage);
      localStorage.setItem('language', newLanguage);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    availableLanguages: Object.keys(translations)
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}